import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './PPADReport.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import { toast } from 'react-toastify';

const API_BASE_URL = '/api/documentation/jobs/ppad-report/';

const SECTION_OPTIONS = [
  { id: 'ALL', text: 'ALL SECTIONS' },
  { id: 'SEA', text: 'ASSESSMENTS BY SEA' },
  { id: 'AIR', text: 'ASSESSMENTS BY AIR' },
  { id: 'ROAD', text: 'ASSESSMENTS BY ROAD' },
  { id: 'EX BOND', text: 'EX BOND' },
];

const STATUS_OPTIONS = [
  { id: 'ALL', text: 'ALL STATUSES' },
  { id: 'PENDING', text: 'PENDING ASSESSMENT' },
  { id: 'PROBLEMS', text: 'VALUE/HS CODE PROBLEMS' },
  { id: 'APPEALED', text: 'APPEALED ASSESSMENT' },
  { id: 'AMENDED', text: 'AMENDED ASSESSMENT' },
  { id: 'CLEARANCE', text: 'CLEARANCE SUSPENSION NOTICE' },
  { id: 'CORRECTION', text: 'CORRECTION QUERY' },
  { id: 'PREVERIFICATION', text: 'PRE-VERIFICATION' },
  { id: 'CANCELLATION', text: 'ASSESSMENT CANCELLATION' },
  { id: 'ACCEPTED', text: 'ASSESSMENT ACCEPTED' },
];

const MODES = [
  { id: 'SEA', text: 'ASSESSMENTS BY SEA' },
  { id: 'AIR', text: 'ASSESSMENTS BY AIR' },
  { id: 'ROAD', text: 'ASSESSMENTS BY ROAD' },
  { id: 'EX BOND', text: 'EX BOND ASSESSMENTS' },
];

const REMARK_STATUSES = [
  'CORRECTION',
  'AMENDED',
  'CLEARANCE',
  'PROBLEMS'
];

const STATUS_COLORS = {
  APPEALED: '#f53c3cff',
  AMENDED: '#919e91ff',
  CLEARANCE: '#ddd65aff',
  CORRECTION: '#54d7e0ff',
  PREVERIFICATION: '#f09cdeff',
  CANCELLATION: '#6ced3dff',
  PROBLEMS: '#eda43dff',
};

const COLUMNS = [
  { key: 'idf_no', label: 'IDF NO.' },
  { key: 'customer_name', label: 'CUSTOMER NAME' },
  { key: 'tansad_no', label: 'TANSAD NO.' },
  { key: 'date', label: 'DATE' },
  { key: 'status', label: 'STATUS' },
  { key: 'status_date', label: 'STATUS DATE' },
  { key: 'remarks', label: 'REMARKS' },
  { key: 'save', label: 'SAVE' },
];

const ACCEPTED_COLUMNS = [
  { key: 'idf_no', label: 'IDF NO.' },
  { key: 'customer_name', label: 'CUSTOMER NAME' },
  { key: 'tansad_no', label: 'TANSAD NO.' },
  { key: 'date', label: 'DATE' },
  { key: 'remarks', label: 'REMARKS' },
  { key: 'done', label: 'DONE' },
  { key: 'save', label: 'SAVE' },
];

const getAuthHeaders = (isFormData = false) => {
  const headers = {
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
  };
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const filterValidJobs = jobs =>
  jobs.filter(j => j.idf_no && j.tansad_no);

const getFlashInfo = (job) => {
  const fmt = (d) => {
    if (!d) return '';
    // Expecting yyyy-mm-dd; leave as-is or format
    return d;
  };
  if (job.berthing_date) {
    return {
      cls: 'flash-red',
      title: `Vessel Berthed on ${fmt(job.berthing_date)}`
    };
  }
  if (job.arrival_date) {
    return {
      cls: 'flash-orange',
      title: `Vessel Arrived on ${fmt(job.arrival_date)}`
    };
  }
  return { cls: '', title: '' };
};

const SortableTableSection = ({ jobs, label, headerBg, onEdit, onSave, hasPermission }) => {
  const { items: sortedJobs, requestSort, getSortIndicator } = useSortableTable(jobs);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 'bold', background: headerBg, padding: 8 }}>{label}</div>
      <div>
        <table style={{ minWidth: '500px', marginBottom: 8, overflow: 'visible' }}>
        <thead>
          <tr>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                onClick={() => requestSort(col.key)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {col.label}{getSortIndicator(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedJobs.length === 0 ? (
            <tr><td colSpan={COLUMNS.length}>NO RECORDS FOUND.</td></tr>
          ) : sortedJobs.map(job => {
            const edit = job.edit || {};
            const currentStatus = (edit.status || job.status || '').toUpperCase();
            const showRemarks = REMARK_STATUSES.includes(currentStatus);
            return (
              <tr
                key={job.id}
                className={getFlashInfo(job).cls}
                title={getFlashInfo(job).title}
              >
                <td>{job.idf_no}</td>
                <td><strong>{job.customer_name}</strong></td>
                <td>{job.tansad_no}</td>
                <td>{job.status_date || ''}</td>
                <td>
                  <Select2Dropdown
                    options={STATUS_OPTIONS.slice(1)}
                    value={edit.status || job.status}
                    onChange={val => onEdit(job.id, 'status', val)}
                    placeholder="SELECT STATUS"
                    displayKey="text"
                    valueKey="id"
                    clearable={false}
                    required={true}
                    disabled={!hasPermission('P-PAD REPORT', 'can_edit')}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    value={edit.status_date || new Date().toISOString().slice(0, 10)}
                    min={new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={e => onEdit(job.id, 'status_date', e.target.value)}
                    style={{ textTransform: 'uppercase' }}
                    disabled={!hasPermission('P-PAD REPORT', 'can_edit')}
                  />
                </td>
                <td>
                  {showRemarks ? (
                    <input
                      type="text"
                      value={edit.remarks || job.remarks || ''}
                      onChange={e => onEdit(job.id, 'remarks', e.target.value)}
                      placeholder="REMARKS"
                      style={{ textTransform: 'uppercase' }}
                      readOnly={!hasPermission('P-PAD REPORT', 'can_edit')}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                {hasPermission('P-PAD REPORT', 'can_edit') && (
                  <td>
                    <button onClick={() => onSave(job.id)} style={{ textTransform: 'uppercase' }}>SAVE</button>
                  </td>
                )}
              </tr>
            );
          })}
          
        </tbody>
      </table>
      </div>
    </div>
  );
};

const PPADReport = () => {
  const { hasPermission } = usePermissions(); // 2. Get the permission checker function
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState({});
  const [sectionFilter, setSectionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [doneCheckboxes, setDoneCheckboxes] = useState({});
  const [doneConfirmDialog, setDoneConfirmDialog] = useState({ show: false, jobId: null });

  const fetchJobs = useCallback(async () => {
    const url = API_BASE_URL;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    const result = await response.json();
    setJobs(result);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);


  const validJobs = filterValidJobs(jobs);

  // Filter accepted jobs for ReturnToPPad items at top level
  const acceptedJobs = validJobs.filter(j => j.return_to_ppad);
  const { items: sortedAcceptedJobs, requestSort: requestSortAccepted, getSortIndicator: getSortIndicatorAccepted } = useSortableTable(acceptedJobs);

  const handleEdit = (jobId, field, value) => {
    setEditing(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], [field]: value }
    }));
  };

  const saveStatus = async (jobId) => {
    // Add a permission check before saving
    if (!hasPermission('P-PAD REPORT', 'can_edit')) {
        // This is a safeguard, as the button should not be visible.
        return;
    }
    const edit = editing[jobId] || {};
    const job = jobs.find(j => j.id === jobId);
    const status = edit.status ?? job.status;
    const status_date = edit.status_date ?? new Date().toISOString().slice(0, 10);
    const remarks = REMARK_STATUSES.includes((status || '').toUpperCase()) ? (edit.remarks ?? job.remarks ?? '') : '';

    try {
      await fetch(`/api/documentation/jobs/${jobId}/update_assessment_status/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status,
          date: status_date,
          remarks
        }),
      });
      await fetchJobs();
      setEditing(prev => ({ ...prev, [jobId]: undefined }));
      toast.success('Saved successfully');
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  // Handle DONE checkbox
  const handleDoneChange = (jobId, checked) => {
    if (checked) {
      setDoneConfirmDialog({ show: true, jobId });
    } else {
      setDoneCheckboxes(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const handleConfirmDone = async () => {
    const { jobId } = doneConfirmDialog;
    if (!jobId) return;

    try {
      await fetch(`/api/documentation/jobs/${jobId}/update_assessment_status/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ return_to_ppad: false, date: new Date().toISOString().slice(0, 10) }),
      });
      await fetchJobs();
      setDoneCheckboxes(prev => ({ ...prev, [jobId]: false }));
      toast.success('Marked as done successfully');
    } catch (e) {
      toast.error('Failed to mark as done');
    }
    setDoneConfirmDialog({ show: false, jobId: null });
  };

  // Handle SAVE button in ACCEPTED section
  const handleAcceptedSave = async (jobId) => {
    if (!hasPermission('P-PAD REPORT', 'can_edit')) {
      return;
    }
    const job = jobs.find(j => j.id === jobId);
    const edit = editing[jobId] || {};
    const remarks = edit.remarks ?? job.remarks ?? '';

    try {
      await fetch(`/api/documentation/jobs/${jobId}/update_assessment_status/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: job.status,
          date: job.status_date,
          remarks,
          return_to_ppad: true,
        }),
      });
      await fetchJobs();
      setEditing(prev => ({ ...prev, [jobId]: undefined }));
      toast.success('Saved successfully');
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  // Render ACCEPTED section for ReturnToPPad items
  const renderAcceptedSection = () => {
    if (sortedAcceptedJobs.length === 0) return null;

    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 'bold', background: '#c2fcc2ff', padding: 8, color: '#2e7d32' }}>AMENDED AFTER ACCEPTED</div>
        <div>
          <table style={{ width: '100%', marginBottom: 8, overflow: 'visible' }}>
          <thead>
            <tr>
              {ACCEPTED_COLUMNS.map(col => (
                <th
                  key={col.key}
                  onClick={() => requestSortAccepted(col.key)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  {col.label}{getSortIndicatorAccepted(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedAcceptedJobs.length === 0 ? (
              <tr><td colSpan={ACCEPTED_COLUMNS.length}>NO RECORDS FOUND.</td></tr>
            ) : sortedAcceptedJobs.map(job => (
              <tr key={job.id}>
                <td>{job.idf_no}</td>
                <td><strong>{job.customer_name}</strong></td>
                <td>{job.tansad_no}</td>
                <td>{job.status_date || ''}</td>
                <td>
                  <input
                    type="text"
                    value={editing[job.id]?.remarks || job.remarks || ''}
                    onChange={e => handleEdit(job.id, 'remarks', e.target.value)}
                    placeholder="REMARKS"
                    style={{ textTransform: 'uppercase' }}
                    readOnly={!hasPermission('P-PAD REPORT', 'can_edit')}
                  />
                </td>
                <td>
                  {hasPermission('P-PAD REPORT', 'can_edit') && (
                    <input
                      type="checkbox"
                      checked={!!doneCheckboxes[job.id]}
                      onChange={e => handleDoneChange(job.id, e.target.checked)}
                    />
                  )}
                </td>
                <td>
                  {hasPermission('P-PAD REPORT', 'can_edit') && (
                    <button
                      onClick={() => handleAcceptedSave(job.id)}
                      style={{ textTransform: 'uppercase', fontSize: 12 }}
                    >
                      SAVE
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  // Left side: Pending by section
  const renderPendingSection = (mode, label) => {
    const sectionJobs = validJobs.filter(
      j => (j.transport_mode || '').toUpperCase() === mode && (j.status || '').toUpperCase() === 'PENDING'
    );
    const jobsWithEdit = sectionJobs.map(job => ({
      ...job,
      edit: editing[job.id] || {}
    }));
    return (
      <SortableTableSection
        key={mode}
        jobs={jobsWithEdit}
        label={label}
        headerBg="#e8f0fe"
        onEdit={handleEdit}
        onSave={saveStatus}
        hasPermission={hasPermission}
      />
    );
  };

  // Right side: Other statuses, each as a section
  const otherStatuses = STATUS_OPTIONS
    .map(opt => opt.id)
    .filter(s => s !== 'PENDING' && s !== 'ALL' && s !== 'ACCEPTED' && s !== 'SENT');

  const renderStatusSection = status => {
    const statusJobs = validJobs.filter(j => (j.status || '').toUpperCase() === status);
    if (statusJobs.length === 0) return null;
    const jobsWithEdit = statusJobs.map(job => ({
      ...job,
      edit: editing[job.id] || {}
    }));
    const label = STATUS_OPTIONS.find(opt => opt.id === status)?.text || status;
    const headerBg = STATUS_COLORS[status] || '#f0f0f0';
    return (
      <SortableTableSection
        key={status}
        jobs={jobsWithEdit}
        label={label}
        headerBg={headerBg}
        onEdit={handleEdit}
        onSave={saveStatus}
        hasPermission={hasPermission}
      />
    );
  };

  // Export to Excel
  const exportExcel = () => {
    const exportData = [];
    MODES.forEach(mode => {
      const sectionJobs = validJobs.filter(
        j => (j.transport_mode || '').toUpperCase() === mode && (j.status || '').toUpperCase() === 'PENDING'
      );
      sectionJobs.forEach(job => {
        exportData.push({
          'IDF NO': job.idf_no,
          'CUSTOMER NAME': job.customer_name,
          'TANSAD NO': job.tansad_no,
          'DATE': job.status_date || '',
          'STATUS': job.status,
          'STATUS DATE': job.status_date || '',
          'REMARKS': job.remarks || '',
        });
      });
    });
    otherStatuses.forEach(status => {
      const statusJobs = validJobs.filter(j => (j.status || '').toUpperCase() === status);
      statusJobs.forEach(job => {
        exportData.push({
          'IDF NO': job.idf_no,
          'CUSTOMER NAME': job.customer_name,
          'TANSAD NO': job.tansad_no,
          'DATE': job.status_date || '',
          'STATUS': job.status,
          'STATUS DATE': job.status_date || '',
          'REMARKS': job.remarks || '',
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PPAD REPORT");
    XLSX.writeFile(wb, "PPAD_Report.xlsx");
  };

  // Export to PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("P-PAD REPORT", 14, 10);
    const body = [];
    MODES.forEach(mode => {
      const sectionJobs = validJobs.filter(
        j => (j.transport_mode || '').toUpperCase() === mode && (j.status || '').toUpperCase() === 'PENDING'
      );
      sectionJobs.forEach(job => {
        body.push([
          job.idf_no,
          job.customer_name,
          job.tansad_no,
          job.status_date || '',
          job.status,
          job.status_date || '',
          job.remarks || '',
        ]);
      });
    });
    otherStatuses.forEach(status => {
      const statusJobs = validJobs.filter(j => (j.status || '').toUpperCase() === status);
      statusJobs.forEach(job => {
        body.push([
          job.idf_no,
          job.customer_name,
          job.tansad_no,
          job.status_date || '',
          job.status,
          job.status_date || '',
          job.remarks || '',
        ]);
      });
    });
    doc.autoTable({
      head: [['IDF NO', 'CUSTOMER NAME', 'TANSAD NO', 'DATE', 'STATUS', 'STATUS DATE', 'REMARKS']],
      body,
      startY: 18,
      styles: { fontSize: 8, textTransform: 'uppercase' }
    });
    doc.save("PPAD_Report.pdf");
  };

  // 11. Add a top-level permission check for viewing the report
  if (!hasPermission('P-PAD REPORT', 'can_view')) {
    return (
        <div className="ppad-report-container">
            <h2>Permission Denied</h2>
            <p>You do not have permission to view this report.</p>
        </div>
    );
  }

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <div className="ppad-report-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ textTransform: 'uppercase', margin: 0 }}>P-PAD REPORT</h2>
          <button onClick={fetchJobs} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
        </div>
      <div className="toolbar" style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <div style={{ minWidth: 220 }}>
          <Select2Dropdown
            options={SECTION_OPTIONS}
            value={sectionFilter}
            onChange={setSectionFilter}
            placeholder="SELECT SECTION"
            displayKey="text"
            valueKey="id"
            clearable={false}
            required={true}
            disabled={false}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select2Dropdown
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="SELECT STATUS"
            displayKey="text"
            valueKey="id"
            clearable={false}
            required={true}
            disabled={false}
          />
        </div>
        <button onClick={exportExcel} style={{ textTransform: 'uppercase' }}>Export Excel</button>
        <button onClick={exportPDF} style={{ textTransform: 'uppercase' }}>Export PDF</button>
      </div>
      <div style={{ display: 'flex', gap: 32 }}>
        {/* Left side: Pending by section */}
        <div style={{ flex: 1 }}>
          {MODES.map(modeObj => renderPendingSection(modeObj.id, modeObj.text))}
        </div>
        {/* Right side: Other statuses and ACCEPTED section */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            {otherStatuses.map(renderStatusSection)}
          </div>
          {/* ACCEPTED section for ReturnToPPad items */}
          <div>
            {renderAcceptedSection()}
          </div>
        </div>
      </div>
      {/* DONE Confirmation Dialog */}
      {doneConfirmDialog.show && (
        <div className="modal-overlay">
          <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>
            <div className="dialog-header" style={{ background: '#4caf50', color: '#fff', borderRadius: '4px 4px 0 0' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>Mark as Done</h3>
            </div>
            <div className="dialog-body" style={{ padding: '20px' }}>
              <p style={{ marginBottom: '10px', fontSize: '1rem' }}>
                Are you sure this is Done?
              </p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-primary" onClick={handleConfirmDone}>
                Yes, Mark as Done
              </button>
              <button className="dialog-btn dialog-btn-secondary" onClick={() => setDoneConfirmDialog({ show: false, jobId: null })}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PPADReport;